import { Graph } from './Graph';
import { PriorityQueue } from '../util';
import { vec3 } from 'gl-matrix';

export type AStarSearchOptions<T> = {
  /**
   * Hard cap on the number of nodes popped from the frontier. Prevents
   * pathological full-graph traversal on large unreachable cases. When
   * exceeded, the search returns `solved: false`.
   */
  maxExpansions?: number;
  /**
   * Optional spatial radius limit. Neighbours whose position lies further
   * than `maxRadius` from `originPosition` will not be enqueued.
   *
   * Both `originPosition` and `getPosition` must be supplied for the
   * radius limit to take effect.
   */
  maxRadius?: number;
  originPosition?: vec3 | number[];
  getPosition?: (node: T) => vec3 | number[];
};

export type AStarSearchResult<T> = {
  cameFrom: Map<T, T>;
  solved: boolean;
  expansions: number;
  /** True when the search aborted due to maxExpansions. */
  exhausted: boolean;
};

export class AStarSearch<T> {
  private heuristic: (a: T, b: T) => number;
  private graph: Graph<T>;

  constructor(graph: Graph<T>, heuristic: (a: T, b: T) => number) {
    this.graph = graph;
    this.heuristic = heuristic;
  }

  public search(start: T, goal: T, options: AStarSearchOptions<T> = {}): AStarSearchResult<T> {
    if (!start) throw Error('start node must be set');
    if (!goal) throw Error('goal node must be set');

    const maxExpansions = options.maxExpansions ?? Infinity;
    const radiusActive =
      options.maxRadius != null && options.originPosition != null && options.getPosition != null;
    const maxRadiusSq = radiusActive ? options.maxRadius! * options.maxRadius! : 0;
    const originPosition = options.originPosition;
    const getPosition = options.getPosition;

    let mapCameFrom = new Map<T, T>();
    let mapCostsSoFar = new Map<T, number>();

    mapCameFrom.set(start, start);
    mapCostsSoFar.set(start, 0);

    let frontier = new PriorityQueue<T>(false);
    frontier.enqueue(start, 0);

    let solved = false;
    let expansions = 0;
    let exhausted = false;

    while (!frontier.isEmpty) {
      let current = frontier.dequeue();
      if (!current) break;

      expansions++;
      if (expansions > maxExpansions) {
        exhausted = true;
        break;
      }

      if (current === goal) {
        solved = true;
        break;
      }

      let neighbours = this.graph.getNeighbours(current);

      for (let next of neighbours) {
        let newCost = mapCostsSoFar.get(current)! + this.graph.getCost(current, next);

        if (!mapCostsSoFar.has(next) || newCost < mapCostsSoFar.get(next)!) {
          if (radiusActive && next !== goal) {
            const pos = getPosition!(next);
            const dx = pos[0] - originPosition![0];
            const dy = pos[1] - originPosition![1];
            const dz = pos[2] - originPosition![2];
            if (dx * dx + dy * dy + dz * dz > maxRadiusSq) {
              continue;
            }
          }

          mapCostsSoFar.set(next, newCost);

          let priority = newCost;
          if (this.heuristic) {
            priority += this.heuristic(next, goal); // Are we moving somewhat in the right direction?
          }
          frontier.enqueue(next, priority);

          mapCameFrom.set(next, current);
        }
      }
    }

    return {
      cameFrom: mapCameFrom,
      solved,
      expansions,
      exhausted,
    };
  }
}
